import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { CircularProgress, Container, FormControl, Grid, MenuItem, Select } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Breadcrumb from "../../components/Breadcrumbs";
import Notification from "../../components/Notification/Notification";
import Pagination from "../../components/Pagination";
import { styles as filterStyles } from "../../components/Search/Filters/styles";
import UserPermission from "../../components/UserPermission";
import api from "../../services/api";
import "../Search/styles.css";
import InfoServiceOrder from "./InfoServiceOrder";

const classes = {
    /* Estilização do conteúdo dentro do container */
    paper: {
        padding: 2,
        display: "inline-block",
        overflow: "auto",
        flexDirection: "row",
        minWidth: "100%",
        backgroundColor: "#F5F5F5",
        boxShadow: "none",
        borderRadius: 5,
        marginBottom: 20,
    },

    title: {
        margin: 8,
        color: "#103168",
        fontWeight: 650,
        marginBottom: 30,
        fontFamily: "Rawline",
    },

    textDescriptionSecond: {
        margin: 1,
        color: "rgb(120, 120, 120)",
        marginTop: "80px",
        marginBottom: "80px",
    },
    buttonSearch: {
        backgroundColor: "#1351B4",
        border: 0,
        borderRadius: "8px",
        color: "white",
        fontWeight: 700,
        marginTop: 5,
        marginBottom: 5,
        marginLeft: 10,
        display: "inline-block",
        textAlign: "center",
        textDecoration: "none",
        fontSize: "15px",
        lineHeight: "40px",
        transition: "filter 0.2",
    },
    inputs: {
        display: "flex",
        alignItems: "center",
    },
    message: {
        padding: 10,
        backgroundColor: "#FFFBE8",
        color: "rgba(0,0,0,0.8)",
        borderRadius: 7,
        width: "100%",
    },
    containerLoading: {
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
    },
};

const priorities = [
    { value: 'NORMAL', label: 'Normal' },
    { value: 'URGENT', label: 'Urgente' },
    { value: 'EMERGENCY', label: 'Emergencial' },
];

const statuses = [
    { value: 'OPEN', label: 'Aberta' },
    { value: 'IN_PROGRESS', label: 'Em Andamento' },
    { value: 'COMPLETED', label: 'Concluída' },
];

const zones = [
    { value: 'URBAN', label: 'Urbana' },
    { value: 'RURAL', label: 'Rural' },
];

const paths = [
    { href: '/manage-service-orders', name: 'Ocorrências Internas' },
    { href: '', name: 'Gerenciar Ocorrências' },
];

export default function ManageServiceOrders() {

    const userPermission = UserPermission();
    const navigate = useNavigate();

    const [notify, setNotify] = useState({ isOpen: false, message: '', type: '', title: '' });
    const [serviceOrders, setServiceOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    /* Filtros */
    const [searchOrderNumber, setSearchOrderNumber] = useState('');
    const [search, setSearch] = useState('');
    const [priority, setPriority] = useState('Todos');
    const [status, setStatus] = useState('Todos');
    const [zone, setZone] = useState('Todas');
    const [neighborhood, setNeighborhood] = useState('Todos');

    /* Paginação */
    const [limit, setLimit] = useState(12);
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        if (userPermission !== 'admin') {
            toast.error("Seu usuário não possuí privilégios para acessar essa página!");
            navigate("/search");
            return;
        }

        api.get('/service-orders')
            .then(response => {
                setServiceOrders(response.data);
            })
            .catch(err => {
                setNotify({
                    isOpen: true,
                    message: 'Erro ao carregar as ocorrências internas, tente novamente.',
                    type: 'error',
                    title: 'Erro'
                });
            })
            .finally(() => setIsLoading(false));
    }, [userPermission, navigate]);

    /* Bairros disponíveis nas ocorrências cadastradas */
    const neighborhoods = useMemo(() => {
        const all = serviceOrders
            .map(serviceOrder => serviceOrder.location?.neighborhood)
            .filter(item => !!item);

        return [...new Set(all)].sort();
    }, [serviceOrders]);

    const filteredServiceOrders = useMemo(() => (
        serviceOrders.filter(serviceOrder => {
            if (search && !(serviceOrder.orderNumber ?? '').toLowerCase().includes(search.toLowerCase())) return false;
            if (priority !== 'Todos' && serviceOrder.priority !== priority) return false;
            if (status !== 'Todos' && serviceOrder.status !== status) return false;
            if (zone !== 'Todas' && serviceOrder.location?.zone !== zone) return false;
            if (neighborhood !== 'Todos' && serviceOrder.location?.neighborhood !== neighborhood) return false;
            return true;
        })
    ), [serviceOrders, search, priority, status, zone, neighborhood]);

    const total = filteredServiceOrders.length;
    const paginatedServiceOrders = filteredServiceOrders.slice(offset, offset + limit);

    if (userPermission !== 'admin') {
        return null;
    }

    return (
        <div style={{ width: "96%", margin: "auto" }}>
            <div style={{ marginTop: 32, marginBottom: 20 }}>
                <Breadcrumb paths={paths} />
            </div>

            <Paper style={classes.paper} elevation={1}>
                <div className="denuncias-container">
                    <div>
                        <div className="search">
                            <Typography variant="h5" align="center" style={classes.title}>
                                Gerenciar Ocorrências Internas
                            </Typography>

                            <div style={classes.message}>
                                Digite abaixo o número da ordem de serviço que você gostaria de
                                visualizar.
                            </div>

                            <div
                                id="input-text-search"
                                style={classes.inputs}
                                className="search-input"
                            >
                                <input
                                    className="input-text-search"
                                    placeholder="Pesquise uma ocorrência interna"
                                    type="search"
                                    value={searchOrderNumber}
                                    onChange={(ev) => setSearchOrderNumber(ev.target.value)}
                                />

                                <Button
                                    className="button-search"
                                    style={classes.buttonSearch}
                                    variant="outlined"
                                    type="button"
                                    onClick={() => {
                                        setSearch(searchOrderNumber);
                                        setOffset(0);
                                    }}
                                >
                                    <SearchIcon style={{ color: "#fff" }} />
                                </Button>
                            </div>
                        </div>

                        <div id="complaints-filter" className="filtros">
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                                    <Typography style={filterStyles.labelInput}>
                                        Quantidade de itens exibidos:*
                                    </Typography>
                                    <FormControl variant="outlined" style={filterStyles.formControl}>
                                        <Select
                                            onChange={e => { setLimit(e.target.value); setOffset(0); }}
                                            value={limit}
                                            style={{ 'background': '#fff' }}
                                        >
                                            {[12, 24, 48].map(item => (
                                                <MenuItem key={item} value={item}>{item}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                                    <Typography style={filterStyles.labelInput}>
                                        Prioridade dos itens exibidos:*
                                    </Typography>
                                    <FormControl variant="outlined" style={filterStyles.formControl}>
                                        <Select
                                            onChange={e => { setPriority(e.target.value); setOffset(0); }}
                                            value={priority}
                                            style={{ 'background': '#fff' }}
                                        >
                                            <MenuItem value="Todos">Todos</MenuItem>
                                            {priorities.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                                    <Typography style={filterStyles.labelInput}>
                                        Status dos itens exibidos:*
                                    </Typography>
                                    <FormControl variant="outlined" style={filterStyles.formControl}>
                                        <Select
                                            onChange={e => { setStatus(e.target.value); setOffset(0); }}
                                            value={status}
                                            style={{ 'background': '#fff' }}
                                        >
                                            <MenuItem value="Todos">Todos</MenuItem>
                                            {statuses.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                                    <Typography style={filterStyles.labelInput}>
                                        Zona dos itens exibidos:*
                                    </Typography>
                                    <FormControl variant="outlined" style={filterStyles.formControl}>
                                        <Select
                                            onChange={e => { setZone(e.target.value); setOffset(0); }}
                                            value={zone}
                                            style={{ 'background': '#fff' }}
                                        >
                                            <MenuItem value="Todas">Todas</MenuItem>
                                            {zones.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                                    <Typography style={filterStyles.labelInput}>
                                        Bairro dos itens exibidos:*
                                    </Typography>
                                    <FormControl variant="outlined" style={filterStyles.formControl}>
                                        <Select
                                            onChange={e => { setNeighborhood(e.target.value); setOffset(0); }}
                                            value={neighborhood}
                                            style={{ 'background': '#fff' }}
                                        >
                                            <MenuItem value="Todos">Todos</MenuItem>
                                            {neighborhoods.map(item => (
                                                <MenuItem key={item} value={item}>{item}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </div>

                        <div style={{ marginTop: 30 }}>
                            <Pagination
                                className="pagination"
                                limit={limit}
                                total={total}
                                offset={offset}
                                setOffset={setOffset}
                            />
                        </div>

                        {isLoading ? (
                            <Container style={classes.containerLoading}>
                                <CircularProgress size={80} thickness={5} />
                                <Typography variant="h6" style={{ marginTop: 20 }}>
                                    Carregando...
                                </Typography>
                            </Container>
                        ) : paginatedServiceOrders.length > 0 ? (
                            <InfoServiceOrder serviceOrders={paginatedServiceOrders} />
                        ) : (
                            <div id="allcomplaints">
                                <Typography
                                    variant="h6"
                                    align="center"
                                    style={classes.textDescriptionSecond}
                                >
                                    Não há ocorrências internas!
                                </Typography>
                            </div>
                        )}

                        <Pagination
                            className="pagination"
                            limit={limit}
                            total={total}
                            offset={offset}
                            setOffset={setOffset}
                        />

                        <div
                            style={{
                                width: "100%",
                                textAlign: "center",
                                marginTop: 10,
                                color: "#1351B4",
                                fontWeight: 500,
                            }}
                        >
                            Total de registros encontrados: {total}
                        </div>

                        <Notification
                            notify={notify}
                            setNotify={setNotify}
                        />
                    </div>
                </div>
            </Paper>
        </div>
    );
}
